/**
 * Instagram Java - Secure Feed with Server-Side Proxy
 * No tokens exposed to client-side
 */

class InstagramFeed {
  constructor() {
    this.section = document.querySelector('.insta--java');
    if (!this.section) return;

    this.grid = this.section.querySelector('[data-grid]');
    this.manualGrid = this.section.querySelector('[data-manual-grid]');
    this.skeletonGrid = this.section.querySelector('[data-skeleton-grid]');
    
    this.config = {
      proxyUrl: this.section.dataset.proxy || '',
      limit: parseInt(this.section.dataset.limit) || 8,
      aspectRatio: this.section.dataset.aspect || 'square',
      showCaptions: this.section.dataset.captions === 'true',
      openNewTab: this.section.dataset.newTab === 'true',
      allowVideo: this.section.dataset.allowVideo === 'true'
    };

    this.fetchTimeout = 6000; // 6 seconds
    this.retryAttempts = 2;
    
    this.init();
  }

  init() {
    if (this.config.proxyUrl) {
      this.fetchInstagramFeed();
    } else {
      this.showFallback();
    }
  }

  async fetchInstagramFeed() {
    try {
      console.log('Fetching Instagram feed from:', this.config.proxyUrl);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.fetchTimeout);

      const response = await fetch(this.config.proxyUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format');
      }

      this.renderFeed(data.data);
      
    } catch (error) {
      console.warn('Instagram feed fetch failed:', error.message);
      this.showFallback();
    }
  }

  renderFeed(posts) {
    if (!posts || posts.length === 0) {
      this.showFallback();
      return;
    }

    // Hide skeleton
    if (this.skeletonGrid) {
      this.skeletonGrid.style.display = 'none';
    }

    // Clear existing content
    this.grid.innerHTML = '';

    // Limit posts and render
    const limitedPosts = posts.slice(0, this.config.limit);
    
    limitedPosts.forEach((post, index) => {
      const item = this.createPostItem(post, index);
      if (item) {
        this.grid.appendChild(item);
      }
    });

    // Show the grid
    this.grid.style.display = 'grid';
    
    // Setup lazy loading if IntersectionObserver is available
    if ('IntersectionObserver' in window) {
      this.setupLazyLoading();
    }
  }

  createPostItem(post, index) {
    if (!post || !post.id) return null;

    // Sanitize data
    const sanitizedPost = this.sanitizePost(post);
    
    const li = document.createElement('li');
    li.className = `insta--java__item insta--java__item--${this.config.aspectRatio}`;
    li.setAttribute('data-post-id', sanitizedPost.id);

    const link = document.createElement('a');
    link.className = 'insta--java__link';
    link.href = sanitizedPost.permalink || '#';
    link.setAttribute('aria-label', `Instagram post ${index + 1}`);
    
    if (this.config.openNewTab && sanitizedPost.permalink) {
      link.target = '_blank';
      link.rel = 'noopener';
    }

    // Media container
    const mediaDiv = document.createElement('div');
    mediaDiv.className = 'insta--java__media';

    // Determine media URL
    let mediaUrl = sanitizedPost.media_url;
    if (sanitizedPost.media_type === 'VIDEO' && sanitizedPost.thumbnail_url) {
      mediaUrl = sanitizedPost.thumbnail_url;
    }

    if (mediaUrl) {
      const img = document.createElement('img');
      img.className = 'insta--java__image';
      img.src = mediaUrl;
      img.alt = sanitizedPost.caption ? 
        this.truncateText(sanitizedPost.caption, 100) : 
        'Instagram image';
      img.loading = index < 4 ? 'eager' : 'lazy';
      img.decoding = 'async';
      
      // Handle image load errors
      img.onerror = () => {
        img.style.display = 'none';
        mediaDiv.innerHTML = '<div class="insta--java__placeholder">📷</div>';
      };

      mediaDiv.appendChild(img);

      // Add video overlay for video posts
      if (sanitizedPost.media_type === 'VIDEO' && this.config.allowVideo) {
        const videoOverlay = document.createElement('div');
        videoOverlay.className = 'insta--java__video-overlay';
        videoOverlay.innerHTML = '▶';
        mediaDiv.appendChild(videoOverlay);
      }
    } else {
      // Placeholder for missing media
      mediaDiv.innerHTML = '<div class="insta--java__placeholder">📷</div>';
    }

    link.appendChild(mediaDiv);

    // Caption overlay
    if (this.config.showCaptions && sanitizedPost.caption) {
      const captionDiv = document.createElement('div');
      captionDiv.className = 'insta--java__caption';
      
      const captionP = document.createElement('p');
      captionP.textContent = this.truncateText(sanitizedPost.caption, 100);
      captionDiv.appendChild(captionP);
      
      link.appendChild(captionDiv);
    }

    li.appendChild(link);
    return li;
  }

  sanitizePost(post) {
    return {
      id: this.sanitizeString(post.id || ''),
      media_url: this.sanitizeUrl(post.media_url || ''),
      thumbnail_url: this.sanitizeUrl(post.thumbnail_url || ''),
      permalink: this.sanitizeUrl(post.permalink || ''),
      media_type: this.sanitizeString(post.media_type || 'IMAGE'),
      caption: this.sanitizeString(post.caption || ''),
      timestamp: this.sanitizeString(post.timestamp || '')
    };
  }

  sanitizeString(str) {
    if (typeof str !== 'string') return '';
    return str.replace(/[<>\"']/g, '').trim();
  }

  sanitizeUrl(url) {
    if (typeof url !== 'string') return '';
    try {
      const parsed = new URL(url);
      // Only allow https URLs from trusted domains
      if (parsed.protocol === 'https:' && 
          (parsed.hostname.includes('instagram.com') || 
           parsed.hostname.includes('fbcdn.net') ||
           parsed.hostname.includes('cdninstagram.com'))) {
        return parsed.href;
      }
    } catch (e) {
      console.warn('Invalid URL:', url);
    }
    return '';
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  showFallback() {
    console.log('Showing fallback content');
    
    // Hide skeleton
    if (this.skeletonGrid) {
      this.skeletonGrid.style.display = 'none';
    }

    // Hide main grid
    if (this.grid) {
      this.grid.style.display = 'none';
    }

    // Show manual grid if available
    if (this.manualGrid) {
      this.manualGrid.style.display = 'grid';
    } else {
      // Show error message
      this.showError('No Instagram content available');
    }
  }

  showError(message) {
    if (this.skeletonGrid) {
      this.skeletonGrid.style.display = 'none';
    }

    const errorDiv = document.createElement('div');
    errorDiv.className = 'insta--java__error';
    errorDiv.innerHTML = `
      <svg class="insta--java__error-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="m15 9-6 6" stroke="currentColor" stroke-width="2"/>
        <path d="m9 9 6 6" stroke="currentColor" stroke-width="2"/>
      </svg>
      <div class="insta--java__error-message">${message}</div>
      <div class="insta--java__error-details">Please check your proxy configuration</div>
    `;

    this.grid.innerHTML = '';
    this.grid.appendChild(errorDiv);
    this.grid.style.display = 'block';
  }

  setupLazyLoading() {
    const images = this.grid.querySelectorAll('img[loading="lazy"]');
    
    if (images.length === 0) return;

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Add fade-in effect
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.3s ease';
          
          img.addEventListener('load', () => {
            img.style.opacity = '1';
          });
          
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px'
    });

    images.forEach(img => imageObserver.observe(img));
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new InstagramFeed();
});

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InstagramFeed();
  });
} else {
  new InstagramFeed();
}
